import React, { useEffect, useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { isNumber } from "lodash";
import moment from "moment";
import numeral from "numeral";
import { FormControlLabel, Grid, Radio } from "@material-ui/core";
import { ButtonText, LabelText, InputText } from "@loanbossinc/component-library";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import DeleteIcon from "@material-ui/icons/Delete";

import {
  PaddedWrapper,
  ChargeContainer,
  ChargeRow,
  RowContainer,
  StyledBaseChargeInput,
  StyledRadioGroup,
  StyledChargeInput
} from "./styles";

const radioButtonsOptions = [
  { value: "month", label: "Per Month" },
  { value: "year", label: "Per Year" }
];

const MONTHS_IN_YEAR = 12;

const ErrorMessage = ({ message }) => (
  <div
    style={{
      position: "absolute",
      color: "#FF1744",
      fontFamily: "Work Sans",
      fontSize: "9px",
      top: "-10px",
      left: "-5px"
    }}
  >
    {message}
  </div>
);

const ScheduledChargeRow = props => {
  const { handleInputChange, handleDeleteRow, editingSchedule } = props;

  const startDateFormatted = moment.utc(props.startDate).format("MM/DD/YY");
  const endDateFormatted = moment.utc(props.endDate).format("MM/DD/YY");
  const editStartDateToShow = props.startDate ? moment.utc(props.startDate).format("YYYY-MM-DD") : "";
  const editEndDateToShow = props.endDate ? moment.utc(props.endDate).format("YYYY-MM-DD") : "";
  const amountBasedOnPeriod = props.period === "month" ? props.monthlyAmount : props.monthlyAmount * MONTHS_IN_YEAR;
  const amountToShow = amountBasedOnPeriod ? numeral(amountBasedOnPeriod).format("$0,0.00") : "";
  const perSqFootBasedOnPeriod = props.period === "month" ? props.monthlyAmount / props.footage : (props.monthlyAmount / props.footage) * MONTHS_IN_YEAR;
  const perSqFootToShow = perSqFootBasedOnPeriod ? numeral(perSqFootBasedOnPeriod).format("$0,0.00") : "";

  const errorMessageStartDate = props.validationErrors[props.id] && props.validationErrors[props.id][0];
  const errorMessageEndDate = props.validationErrors[props.id] && props.validationErrors[props.id][1];

  const chargeAmountInputValue = amountBasedOnPeriod ? Math.round(amountBasedOnPeriod * 100) / 100 : null;

  return (
    <ChargeContainer style={{ position: "relative" }}>
      <ChargeRow w="28%" align="start">
        {!props.editingSchedule ? (
          startDateFormatted
        ) : (
          <div style={{ position: "relative" }}>
            {errorMessageStartDate && <ErrorMessage message={errorMessageStartDate} />}
            <StyledChargeInput
              name="startDate"
              label=""
              type="date"
              defaultValue={editStartDateToShow}
              onChange={handleInputChange}
              textAlign="left"
              inputProps={{ max: "9999-12-31" }}
              isError={editingSchedule && errorMessageStartDate}
            />
          </div>
        )}
      </ChargeRow>
      <ChargeRow w="28%" align="start">
        {!props.editingSchedule ? (
          endDateFormatted
        ) : (
          <div style={{ position: "relative" }}>
            {errorMessageEndDate && <ErrorMessage message={errorMessageEndDate} />}
            <StyledChargeInput
              name="endDate"
              label=""
              type="date"
              defaultValue={editEndDateToShow}
              onChange={handleInputChange}
              textAlign="left"
              inputProps={{ max: "9999-12-31" }}
              isError={editingSchedule && errorMessageEndDate}
            />
          </div>
        )}
      </ChargeRow>
      <ChargeRow w="20%" align="center">
        {!props.editingSchedule ? (
          amountToShow
        ) : (
          <StyledBaseChargeInput
            onChange={handleInputChange}
            value={chargeAmountInputValue}
            textAlign="center"
            name="monthlyAmount"
            allowNegative={false}
            decimals={2}
            thousandSeparator
          />
        )}
      </ChargeRow>
      <ChargeRow w="20%" align="center">
        {`${perSqFootToShow} /${props.period}`}
      </ChargeRow>
      <ChargeRow w="4%" align="right">
        <DeleteIcon
          style={{
            color: props.editingSchedule ? "#005AA9" : "#9e9e9e",
            position: "absolute",
            right: "5px",
            top: "15px",
            cursor: editingSchedule && "pointer"
          }}
          onClick={props.editingSchedule ? handleDeleteRow : undefined}
        />
      </ChargeRow>
    </ChargeContainer>
  );
};

const ChargeScheduleTypeTable = props => {
  const { leaseState, setLeaseState } = props;
  const { selectedChargeScheduleGroup, squareFootage, editedChargeSchedules, isEditingChargeScheduleTable } =
    leaseState;
  const [period, setPeriod] = useState("month");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => () => {
    setLeaseState(prev => ({
      ...prev,
      selectedChargeScheduleGroup: null,
      editedChargeSchedules: [],
      isEditingChargeScheduleTable: false,
      chargeSchedulesTextInputs: { chargeCodeNickname: "", chargeCode: "" }
    }));
  }, []);

  useEffect(() => {
    if (selectedChargeScheduleGroup) {
      const existingChargeCodeAlias = leaseState.editedChargeCodeAliases?.find(alias => alias.chargeCode === selectedChargeScheduleGroup.chargeCode);
      const aliasToSet = existingChargeCodeAlias ? existingChargeCodeAlias.alias : "";
      setLeaseState(prev => ({
        ...prev,
        chargeSchedulesTextInputs: {
          chargeCodeNickname: aliasToSet,
          chargeCode: selectedChargeScheduleGroup.chargeCode
        },
        editedChargeSchedules: selectedChargeScheduleGroup.charges
      }));
    }
  }, [selectedChargeScheduleGroup]);

  const handlePeriodChange = e => {
    setPeriod(e.target.value);
  };

  const handleChargeCodeTextInputChange = e => {
    const { value } = e.target;
    setLeaseState(prev => ({
      ...prev,
      chargeSchedulesTextInputs: {
        ...prev.chargeSchedulesTextInputs,
        chargeCode: value
      },
      editedChargeSchedules: prev.editedChargeSchedules.map(charge => ({ ...charge, chargeCode: value }))
    }));
  };

  const handleNickNameTextInputChange = e => {
    const { value } = e.target;
    setLeaseState(prev => ({
      ...prev,
      chargeSchedulesTextInputs: {
        ...prev.chargeSchedulesTextInputs,
        chargeCodeNickname: value
      }
    }));
  };

  const isManuallyAdded = () => {
    if (!selectedChargeScheduleGroup) {
      return true;
    }

    return Boolean(selectedChargeScheduleGroup?.charges[0]?.manuallyAdded);
  };

  const validateDateInputs = (date, chargeId, type) => {
    if (moment.utc(date).isValid()) {
      let errorMessage = null;

      const activeCharge = editedChargeSchedules?.find(charge => charge.id === chargeId);

      // check if start is earlier than end
      if (type === "startDate" && moment.utc(activeCharge.endDate).isValid()) {
        if (moment.utc(date).isSameOrAfter(moment.utc(activeCharge.endDate), "day")) {
          errorMessage = "Start must be before end";
        }
      } else {
        // check if end is later than start
        if (moment.utc(activeCharge.startDate).isValid() && moment.utc(date).isSameOrBefore(moment.utc(activeCharge.startDate), "day")) {
          errorMessage = "End must be after start";
        }
      }

      // check if overlaps with other rent
      editedChargeSchedules.forEach(charge => {
        const isOverlapping = moment.utc(date).isBetween(moment.utc(charge.startDate), moment.utc(charge.endDate), "day", "()");
        if (charge.id !== chargeId && isOverlapping && !errorMessage) {
          errorMessage = "Date overlaps with another rent";
        }
      });

      // check if overlaps with lease start/end
      if (!errorMessage) {
        const leaseStart = moment.utc(leaseState.leaseStartDate);
        const leaseEnd = moment.utc(leaseState.leaseEndDate);
        const withinLeaseTerm = moment.utc(date).isBetween(leaseStart, leaseEnd, "day", "[]");
        if (!withinLeaseTerm) {
          errorMessage = "Date is not within lease term";
        }
      }

      const activeErrorsArray = validationErrors[chargeId] || [null, null];
      if (type === "startDate") {
        activeErrorsArray[0] = errorMessage;
      } else {
        activeErrorsArray[1] = errorMessage;
      }
      setValidationErrors({ ...validationErrors, [chargeId]: activeErrorsArray });
    }
  };

  const handleInputChange = (e, chargeId) => {
    const { name, value } = e.target;
    if (name && value !== undefined) {
      setLeaseState(prev => ({
        ...prev,
        editedChargeSchedules: prev.editedChargeSchedules.map(charge => {
          if (charge.id !== chargeId) {
            return charge;
          }
          if (name === "startDate" || name === "endDate") {
            validateDateInputs(value, chargeId, name);

            return {
              ...charge,
              [name]: moment.utc(value).toISOString()
            };
          }

          // charge amount handle change
          let amount = parseFloat(value?.replace(/,/g, ""));
          if (period === "year") {
            amount = amount / MONTHS_IN_YEAR;
          }

          return {
            ...charge,
            monthlyAmount: amount
          };
        })
      }));
    }
  };

  const handleAddRow = () => {
    const emptyRow = {
      id: uuidV4(),
      chargeCode: leaseState.chargeSchedulesTextInputs.chargeCode,
      chargeType: "CAM",
      endDate: null,
      startDate: null,
      monthlyAmount: null,
      manuallyAdded: isManuallyAdded(),
      squareFeet: squareFootage
    };
    setLeaseState(prev => ({
      ...prev,
      editedChargeSchedules: [...prev.editedChargeSchedules, emptyRow],
      isEditingChargeScheduleTable: true
    }));
  };

  const handleDeleteRow = id => {
    setLeaseState(prev => ({
      ...prev,
      editedChargeSchedules: prev.editedChargeSchedules.filter(charge => charge.id !== id)
    }));
  };

  const handleSaveSchedule = () => {
    setLeaseState(prev => ({
      ...prev,
      isAnythingWasUpdated: true,
      isEditingChargeScheduleTable: false
    }));
  };

  const checkIfNoErrors = () => editedChargeSchedules.every(charge => {
    const errors = validationErrors[charge.id];

    return !errors?.some(error => Boolean(error));
  });

  const areLeaseDatesValid = Boolean(leaseState.leaseStartDate) && Boolean(leaseState.leaseEndDate);

  const areRowsValid = editedChargeSchedules.length && editedChargeSchedules.every(charge => {
    const isChargeEntered = isNumber(charge.monthlyAmount) && !Number.isNaN(charge.monthlyAmount);
    const areDatesEntered = moment(charge.startDate).isValid() && moment(charge.endDate).isValid();

    return isChargeEntered && areDatesEntered;
  });

  const isAbleToSaveSchedule = checkIfNoErrors() && areLeaseDatesValid && areRowsValid;

  const getScheduleAction = () => {
    if (!isEditingChargeScheduleTable && !editedChargeSchedules.length) {
      return null;
    }
    if (isEditingChargeScheduleTable) {
      return (
        <ButtonText
          label={
            <div style={{ display: "flex" }}>
              <div style={{ marginRight: "0.3em" }}>Save Schedule</div>
              <CheckIcon
                style={{
                  cursor: "pointer",
                  color: !isAbleToSaveSchedule ? "#9aa2a8" : "#005AA9"
                }}
              />
            </div>
          }
          disabled={!isAbleToSaveSchedule}
          onClick={() => handleSaveSchedule()}
        />
      );
    }

    return (
      <ButtonText
        label={
          <div style={{ display: "flex" }}>
            <div style={{ marginRight: "0.3em" }}>Edit Schedule</div>
            <EditIcon style={{ cursor: "pointer", color: "#005AA9" }} />
          </div>
        }
        onClick={() => {
          setLeaseState(prev => ({ ...prev, isEditingChargeScheduleTable: true }));
        }}
      />
    );
  };

  const getAddNewScheduleRow = isIconNeeded => {
    if (!isEditingChargeScheduleTable && editedChargeSchedules.length) {
      return null;
    }

    return (
      <ChargeContainer>
        <ChargeRow w="80%" align="left">
          <ButtonText onClick={handleAddRow} label="Add scheduled charge" />
        </ChargeRow>
        {isIconNeeded && (
          <ChargeRow w="20%" align="right">
            <EditIcon
              style={{ cursor: "pointer", color: "#005AA9" }}
              onClick={handleAddRow}
            />
          </ChargeRow>
        )}
      </ChargeContainer>
    );
  };

  return (
    <RowContainer>
      <Grid
        container
        justify="flex-start"
        style={{ marginBottom: "10px" }}
        alignItems="center"
        direction="row"
      >
        <Grid item xs={6}>
          <PaddedWrapper>
            <InputText
              onChange={handleNickNameTextInputChange}
              variant="filled"
              value={leaseState.chargeSchedulesTextInputs.chargeCodeNickname}
              name="chargeCodeNickname"
              label="Charge type nickname"
              fullWidth
            />
          </PaddedWrapper>
        </Grid>
        <Grid item xs={6}>
          <PaddedWrapper>
            <InputText
              onChange={handleChargeCodeTextInputChange}
              variant="filled"
              value={leaseState.chargeSchedulesTextInputs.chargeCode}
              name="chargeCode"
              label="Charge type code"
              fullWidth
              disabled={!isManuallyAdded()}
            />
          </PaddedWrapper>
        </Grid>
      </Grid>
      <Grid
        container
        justify="flex-start"
        style={{ marginBottom: "10px" }}
        alignItems="center"
        direction="row"
      >
        <Grid item xs={12}>
          <PaddedWrapper>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <StyledRadioGroup
                value={period}
                aria-label="year-or-month"
                name="period"
                onChange={handlePeriodChange}
              >
                {radioButtonsOptions.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={<LabelText value={option.label} />}
                  />
                ))}
              </StyledRadioGroup>
              {getScheduleAction()}
            </div>
          </PaddedWrapper>
        </Grid>
        <Grid item xs={12}>
          <PaddedWrapper>
            <ChargeContainer header>
              <ChargeRow w="28%" align="start" bold>
                START
              </ChargeRow>
              <ChargeRow w="28%" align="start" bold>
                END
              </ChargeRow>
              <ChargeRow w="20%" align="center" bold>
                CHARGE
              </ChargeRow>
              <ChargeRow w="20%" align="center" bold>
                AMOUNT PER SQ FT
              </ChargeRow>
              <ChargeRow w="4%" align="right" />
            </ChargeContainer>
            {!isEditingChargeScheduleTable && getAddNewScheduleRow(true)}
          </PaddedWrapper>
        </Grid>
        <Grid item xs={12}>
          <PaddedWrapper>
            {isEditingChargeScheduleTable && !editedChargeSchedules.length ?
              getAddNewScheduleRow(false) :
              null}
            {editedChargeSchedules.map(schedule => {
              const { id, startDate, endDate, monthlyAmount } = schedule;

              return (
                <ScheduledChargeRow
                  key={id}
                  id={id}
                  period={period}
                  handleInputChange={e => handleInputChange(e, id)}
                  handleDeleteRow={() => handleDeleteRow(id)}
                  footage={squareFootage}
                  startDate={startDate}
                  endDate={endDate}
                  monthlyAmount={monthlyAmount}
                  editingSchedule={isEditingChargeScheduleTable}
                  validationErrors={validationErrors}
                />
              );
            })}
            {editedChargeSchedules.length ? getAddNewScheduleRow(false) : null}
          </PaddedWrapper>
        </Grid>
      </Grid>
    </RowContainer>
  );
};

export default ChargeScheduleTypeTable;
